import React, { forwardRef } from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'children'
> {
  label?: string;
  options: SelectOption[];
  placeholder?: string;
  hint?: string;
  error?: string;
  optional?: boolean;
}

/**
 * KERN Select Component
 *
 * Usage:
 * <Select
 *   label="Titel"
 *   options={[{value: 'dr', label: 'Dr.'}, {value: 'prof', label: 'Prof.'}]}
 *   placeholder="Bitte auswählen"
 * />
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, options, placeholder, hint, error, optional, id, className = '', ...props }, ref) => {
    const selectId =
      id || (label ? `select-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);
    const hintId = hint && selectId ? `${selectId}-hint` : undefined;
    const errorId = error && selectId ? `${selectId}-error` : undefined;
    const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

    const wrapperClasses = ['kern-form-input', error && 'kern-form-input--error', className]
      .filter(Boolean)
      .join(' ');

    const selectClasses = ['kern-form-input__select', error && 'kern-form-input__select--error']
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClasses}>
        {label && (
          <label className="kern-label" htmlFor={selectId}>
            {label}
            {optional && <span className="kern-label__optional"> - Optional</span>}
          </label>
        )}
        {hint && (
          <div className="kern-hint" id={hintId}>
            {hint}
          </div>
        )}
        <div className="kern-form-input__select-wrapper">
          <select
            ref={ref}
            className={selectClasses}
            id={selectId}
            aria-describedby={describedBy}
            {...props}
          >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {error && (
          <p className="kern-error" id={errorId} role="alert">
            <span className="kern-icon kern-icon--danger kern-icon--md" aria-hidden="true" />
            <span className="kern-body">{error}</span>
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
